/**
 * Financial Estimate Tab Component (FM-1)
 * Sprint 12 - Financial Module MVP
 * 
 * Features:
 * - Cost Categories (licenses, infra, external, internal labor)
 * - Capex/Opex split
 * - Labor model with role rates
 * - Non-labor costs
 * - Benefits (financial & non-financial)
 * - AI-powered "Suggest estimate" button
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, Users, Calculator, Sparkles, Plus, Trash2, Save,
  TrendingUp, AlertCircle, CheckCircle, PieChart, Clock, Edit2,
  ChevronDown, ChevronUp, Loader2, RefreshCw, Info, HelpCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { HelpTooltip } from './HelpTooltip';
import { useCurrency } from '../utils/currencyUtils';

// API client for financial module
const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiGet = async (url, params = {}) => {
  return axios.get(`${API_BASE}${url}`, { headers: getAuthHeaders(), params });
};

const apiPost = async (url, data = {}) => {
  return axios.post(`${API_BASE}${url}`, data, { headers: getAuthHeaders() });
};

// Default labor roles with market rates
const DEFAULT_LABOR_ROLES = [
  { role: 'DEV', label: 'Developer', defaultRate: 125 },
  { role: 'QA', label: 'QA Engineer', defaultRate: 100 },
  { role: 'PM', label: 'Project Manager', defaultRate: 150 },
  { role: 'BA', label: 'Business Analyst', defaultRate: 110 },
  { role: 'ARCH', label: 'Architect', defaultRate: 175 },
  { role: 'DEVOPS', label: 'DevOps Engineer', defaultRate: 140 },
];

// Non-labor cost categories
const COST_CATEGORIES = [
  { id: 'software', label: 'Software/Licenses', icon: '💻' },
  { id: 'hardware', label: 'Hardware/Equipment', icon: '🖥️' },
  { id: 'services', label: 'Professional Services', icon: '👔' },
  { id: 'travel', label: 'Travel & Expenses', icon: '✈️' },
  { id: 'training', label: 'Training', icon: '📚' },
  { id: 'other', label: 'Other', icon: '📦' },
];

// Labor Cost Row Component (uses currency from props)
const LaborCostRow = ({ item, index, onUpdate, onRemove, formatCurrency }) => {
  const [expanded, setExpanded] = useState(false);
  
  const calculateTotal = () => {
    return (item.fte_count || 0) * (item.rate_per_hour || 0) * 
           (item.hours_per_month || 160) * (item.duration_months || 1);
  };

  return (
    <div className="border rounded-lg mb-2 overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span className="font-medium">{item.role || 'New Role'}</span>
          <span className="text-sm text-gray-500">
            {item.fte_count || 0} FTE × {item.duration_months || 1} months
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-green-700">{formatCurrency(calculateTotal())}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(index); }}
            className="p-1 hover:bg-red-100 rounded text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 bg-white">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select
              value={item.role || ''}
              onChange={(e) => {
                const role = DEFAULT_LABOR_ROLES.find(r => r.role === e.target.value);
                onUpdate(index, { 
                  role: e.target.value,
                  rate_per_hour: role?.defaultRate || item.rate_per_hour
                });
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid={`labor-role-${index}`}
            >
              <option value="">Select role</option>
              {DEFAULT_LABOR_ROLES.map(r => (
                <option key={r.role} value={r.role}>{r.label}</option>
              ))}
              <option value="CUSTOM">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">FTE Count</label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={item.fte_count || ''}
              onChange={(e) => onUpdate(index, { fte_count: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="0.0"
              data-testid={`labor-fte-${index}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Rate ($/hr)</label>
            <input
              type="number"
              min="0"
              value={item.rate_per_hour || ''}
              onChange={(e) => onUpdate(index, { rate_per_hour: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="0"
              data-testid={`labor-rate-${index}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Duration (months)</label>
            <input
              type="number"
              min="1"
              value={item.duration_months || ''}
              onChange={(e) => onUpdate(index, { duration_months: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="1"
              data-testid={`labor-duration-${index}`}
            />
          </div>
          <div className="col-span-full">
            <label className="block text-xs font-medium text-gray-600 mb-1">Hours/Month</label>
            <input
              type="number"
              min="0"
              value={item.hours_per_month || 160}
              onChange={(e) => onUpdate(index, { hours_per_month: parseFloat(e.target.value) || 160 })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="160"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Non-Labor Cost Row Component
const NonLaborCostRow = ({ item, index, onUpdate, onRemove, currencySymbol = '$' }) => {
  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg mb-2 bg-white">
      <select
        value={item.category || 'other'}
        onChange={(e) => onUpdate(index, { category: e.target.value })}
        className="px-3 py-2 border rounded-lg text-sm w-36"
        data-testid={`nonlabor-category-${index}`}
      >
        {COST_CATEGORIES.map(c => (
          <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
        ))}
      </select>
      <input
        type="text"
        value={item.name || ''}
        onChange={(e) => onUpdate(index, { name: e.target.value })}
        className="flex-1 px-3 py-2 border rounded-lg text-sm"
        placeholder="Description"
        data-testid={`nonlabor-name-${index}`}
      />
      <input
        type="number"
        min="0"
        value={item.amount || ''}
        onChange={(e) => onUpdate(index, { amount: parseFloat(e.target.value) || 0 })}
        className="w-32 px-3 py-2 border rounded-lg text-sm text-right"
        placeholder={`${currencySymbol}0`}
        data-testid={`nonlabor-amount-${index}`}
      />
      <select
        value={item.type || 'opex'}
        onChange={(e) => onUpdate(index, { type: e.target.value })}
        className="px-3 py-2 border rounded-lg text-sm w-28"
        data-testid={`nonlabor-type-${index}`}
      >
        <option value="opex">OPEX</option>
        <option value="capex">CAPEX</option>
      </select>
      <button
        onClick={() => onRemove(index)}
        className="p-2 hover:bg-red-100 rounded text-red-500"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Benefit Row Component
const BenefitRow = ({ item, index, onUpdate, onRemove, currencySymbol = '$' }) => {
  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg mb-2 bg-white">
      <select
        value={item.benefit_type || 'financial'}
        onChange={(e) => onUpdate(index, { benefit_type: e.target.value })}
        className="px-3 py-2 border rounded-lg text-sm w-32"
        data-testid={`benefit-type-${index}`}
      >
        <option value="financial">Financial</option>
        <option value="non_financial">Non-Financial</option>
      </select>
      <input
        type="text"
        value={item.name || ''}
        onChange={(e) => onUpdate(index, { name: e.target.value })}
        className="flex-1 px-3 py-2 border rounded-lg text-sm"
        placeholder="Benefit description"
        data-testid={`benefit-name-${index}`}
      />
      {item.benefit_type === 'financial' && (
        <input
          type="number"
          min="0"
          value={item.annual_value || ''}
          onChange={(e) => onUpdate(index, { annual_value: parseFloat(e.target.value) || 0 })}
          className="w-32 px-3 py-2 border rounded-lg text-sm text-right"
          placeholder={`${currencySymbol}/year`}
          data-testid={`benefit-value-${index}`}
        />
      )}
      <select
        value={item.confidence || 'medium'}
        onChange={(e) => onUpdate(index, { confidence: e.target.value })}
        className="px-3 py-2 border rounded-lg text-sm w-28"
      >
        <option value="high">High</option>
        <option value="medium">Medium</option>
        <option value="low">Low</option>
      </select>
      <button
        onClick={() => onRemove(index)}
        className="p-2 hover:bg-red-100 rounded text-red-500"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Main Component
const FinancialEstimateTab = ({ scopeId, projectId, onBaselineCreated, readOnly = false }) => {
  const { formatCurrency, currencySymbol } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [laborCosts, setLaborCosts] = useState([]);
  const [nonLaborCosts, setNonLaborCosts] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [assumptions, setAssumptions] = useState([]);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [aiRationale, setAiRationale] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  
  // Resolve scope ID with fallbacks
  const resolveScopeId = useCallback(async () => {
    // If scopeId provided, use it
    if (scopeId) return scopeId;
    
    // Try to get scopeId from projectId
    if (projectId) {
      try {
        const res = await apiGet(`/projects/${projectId}/scope`);
        return res.data?.scope_id || res.data?.id || null;
      } catch (err) {
        console.warn('Could not resolve scope from project:', err);
      }
    }
    
    return null;
  }, [scopeId, projectId]);

  // Fetch estimate with retry/backoff
  const loadEstimate = useCallback(async (retry = 0) => {
    const resolvedScopeId = await resolveScopeId();
    
    if (!resolvedScopeId) {
      setLoading(false);
      setLastError('No scope available for financial estimate');
      return;
    }
    
    setLoading(true);
    setLastError(null);
    
    try {
      const res = await apiGet(`/financial/estimate?scopeId=${resolvedScopeId}`);
      const data = res.data?.estimate || res.data || {};
      
      // Null guards for all data fields
      setEstimate(data);
      setLaborCosts(Array.isArray(data.labor_costs) ? data.labor_costs : []);
      setNonLaborCosts(Array.isArray(data.non_labor_costs) ? data.non_labor_costs : []);
      setBenefits(Array.isArray(data.benefits) ? data.benefits : []);
      setAssumptions(Array.isArray(data.assumptions) ? data.assumptions : []);
      setAiRationale(data.ai_rationale || null);
      setDirty(false);
      setRetryCount(0);
    } catch (err) {
      console.error('Failed to load financial estimate:', err);
      
      // Handle 404 gracefully - no estimate yet
      if (err.response?.status === 404) {
        setEstimate(null);
        setLaborCosts([]);
        setNonLaborCosts([]);
        setBenefits([]);
        setAssumptions([]);
        setLoading(false);
        return;
      }
      
      // Retry logic with exponential backoff
      const maxRetries = 3;
      const backoffDelays = [1000, 2000, 4000]; // 1s, 2s, 4s
      
      if (retry < maxRetries) {
        const delay = backoffDelays[retry] || 4000;
        setRetryCount(retry + 1);
        setLastError(`Load failed. Retrying in ${delay/1000}s... (${retry + 1}/${maxRetries})`);
        
        setTimeout(() => {
          loadEstimate(retry + 1);
        }, delay);
      } else {
        setLastError('Failed to load financial estimate after multiple attempts');
        toast.error('Failed to load financial estimate. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [resolveScopeId]);

  useEffect(() => {
    loadEstimate();
  }, [loadEstimate]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    let totalLabor = 0;
    let totalNonLabor = 0;
    let totalCapex = 0;
    let totalOpex = 0;
    let totalBenefits = 0;

    // Labor costs (all OPEX)
    laborCosts.forEach(item => {
      const total = (item.fte_count || 0) * (item.rate_per_hour || 0) * 
                   (item.hours_per_month || 160) * (item.duration_months || 1);
      totalLabor += total;
      totalOpex += total;
    });

    // Non-labor costs
    nonLaborCosts.forEach(item => {
      const amount = item.amount || 0;
      totalNonLabor += amount;
      if (item.type === 'capex') {
        totalCapex += amount;
      } else {
        totalOpex += amount;
      }
    });

    // Benefits
    benefits.forEach(item => {
      if (item.benefit_type === 'financial') {
        totalBenefits += item.annual_value || 0;
      }
    });

    const totalCost = totalLabor + totalNonLabor;
    const roi = totalCost > 0 ? ((totalBenefits - totalCost) / totalCost) * 100 : 0;
    const paybackMonths = totalBenefits > 0 ? Math.ceil(totalCost / (totalBenefits / 12)) : 0;

    return {
      totalLabor,
      totalNonLabor,
      totalCapex,
      totalOpex,
      totalCost,
      totalBenefits,
      roi: Math.round(roi * 100) / 100,
      paybackMonths
    };
  }, [laborCosts, nonLaborCosts, benefits]);

  const totals = calculateTotals();

  // Save estimate
  const handleSave = async () => {
    if (!scopeId) {
      toast.error('No scope selected');
      return;
    }

    setSaving(true);
    try {
      await apiPost(`/financial/estimate?scopeId=${scopeId}`, {
        labor_costs: laborCosts,
        non_labor_costs: nonLaborCosts,
        benefits: benefits,
        assumptions: assumptions
      });
      toast.success('Financial estimate saved');
      setDirty(false);
      await loadEstimate();
    } catch (err) {
      console.error('Failed to save estimate:', err);
      toast.error(err.response?.data?.detail || 'Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  // AI Suggest
  const handleAiSuggest = async () => {
    if (!scopeId) {
      toast.error('No scope selected');
      return;
    }

    setAiLoading(true);
    try {
      const res = await apiPost(`/financial/estimate/ai-suggest?scopeId=${scopeId}`);
      const data = res.data;
      
      setLaborCosts(data.estimate.labor_costs || []);
      setNonLaborCosts(data.estimate.non_labor_costs || []);
      setBenefits(data.estimate.benefits || []);
      setAssumptions(data.estimate.assumptions || []);
      setAiRationale(data.rationale);
      setDirty(true);
      
      toast.success(`AI generated estimate based on ${data.complexity?.label || 'scope'} complexity`);
    } catch (err) {
      console.error('AI suggestion failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate AI estimate');
    } finally {
      setAiLoading(false);
    }
  };

  // Labor cost handlers
  const updateLaborCost = (index, updates) => {
    setLaborCosts(prev => prev.map((item, i) => i === index ? { ...item, ...updates } : item));
    setDirty(true);
  };

  const addLaborCost = () => {
    setLaborCosts(prev => [...prev, { 
      role: '', 
      fte_count: 1, 
      rate_per_hour: 125, 
      hours_per_month: 160, 
      duration_months: 3 
    }]);
    setDirty(true);
  };

  const removeLaborCost = (index) => {
    setLaborCosts(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  // Non-labor cost handlers
  const updateNonLaborCost = (index, updates) => {
    setNonLaborCosts(prev => prev.map((item, i) => i === index ? { ...item, ...updates } : item));
    setDirty(true);
  };

  const addNonLaborCost = () => {
    setNonLaborCosts(prev => [...prev, { name: '', amount: 0, type: 'opex', category: 'other' }]);
    setDirty(true);
  };

  const removeNonLaborCost = (index) => {
    setNonLaborCosts(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  // Benefit handlers
  const updateBenefit = (index, updates) => {
    setBenefits(prev => prev.map((item, i) => i === index ? { ...item, ...updates } : item));
    setDirty(true);
  };

  const addBenefit = () => {
    setBenefits(prev => [...prev, { name: '', benefit_type: 'financial', annual_value: 0, confidence: 'medium' }]);
    setDirty(true);
  };

  const removeBenefit = (index) => {
    setBenefits(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        {retryCount > 0 && (
          <p className="mt-2 text-sm text-amber-600">
            {lastError}
          </p>
        )}
      </div>
    );
  }
  
  // Show error state with retry option
  if (lastError && !loading) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
        <p className="text-red-600 mb-4">{lastError}</p>
        <button
          onClick={() => loadEstimate(0)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  if (!scopeId && !projectId) {
    return (
      <div className="p-6 text-center text-gray-500">
        <Calculator className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Select a scope or project to view financial estimates</p>
      </div>
    );
  }
  
  // Friendly placeholder when no estimate exists
  if (!estimate && laborCosts.length === 0 && nonLaborCosts.length === 0) {
    return (
      <div className="p-6" data-testid="financial-estimate-empty">
        <div className="text-center bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-8 border border-green-100">
          <Calculator className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Financial Estimate Yet</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create a detailed financial estimate including labor costs, non-labor expenses, and projected benefits.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={handleAiSuggest}
              disabled={aiLoading || readOnly}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 disabled:opacity-50"
              data-testid="ai-suggest-initial"
            >
              <Sparkles className="w-4 h-4" />
              {aiLoading ? 'Generating...' : 'AI Suggest Estimate'}
            </button>
            <button
              onClick={addLaborCost}
              disabled={readOnly}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50"
              data-testid="manual-estimate"
            >
              <Plus className="w-4 h-4" />
              Create Manually
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="financial-estimate-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            Financial Estimate
          </h2>
          <p className="text-sm text-gray-500">Define costs, benefits, and ROI projections</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleAiSuggest}
            disabled={aiLoading || readOnly}
            className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50"
            data-testid="ai-suggest-estimate-btn"
          >
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            AI Suggest
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty || readOnly}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            data-testid="save-estimate-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Estimate
          </button>
        </div>
      </div>

      {/* AI Rationale */}
      {aiRationale && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-800 mb-1">AI Analysis</p>
              <p className="text-sm text-purple-700">{aiRationale}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Calculator className="w-4 h-4" />
            Total Cost
            <HelpTooltip topic="capex" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalCost)}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <span className="text-blue-600">CAPEX:</span> {formatCurrency(totals.totalCapex)} 
            <span className="mx-1">|</span>
            <span className="text-amber-600">OPEX:</span> {formatCurrency(totals.totalOpex)}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Users className="w-4 h-4" />
            Labor Cost
            <HelpTooltip topic="labor" />
          </div>
          <div className="text-2xl font-bold text-blue-600">{formatCurrency(totals.totalLabor)}</div>
          <div className="text-xs text-gray-500 mt-1">{laborCosts.length} roles defined</div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <TrendingUp className="w-4 h-4" />
            Annual Benefits
            <HelpTooltip topic="benefits" />
          </div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(totals.totalBenefits)}</div>
          <div className="text-xs text-gray-500 mt-1">{benefits.filter(b => b.benefit_type === 'financial').length} financial benefits</div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <PieChart className="w-4 h-4" />
            ROI / Payback
            <HelpTooltip topic="roi" />
          </div>
          <div className="text-2xl font-bold" style={{ color: totals.roi >= 0 ? '#16a34a' : '#dc2626' }}>
            {totals.roi.toFixed(1)}%
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {totals.paybackMonths > 0 ? `${totals.paybackMonths} months payback` : 'No payback'}
          </div>
        </div>
      </div>

      {/* Benefits Dashboard - Visual Summary */}
      {benefits.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5" />
            Benefits Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* By Type */}
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-xs text-gray-500 uppercase mb-1">Financial</p>
              <p className="text-xl font-bold text-green-700">
                {formatCurrency(benefits.filter(b => b.benefit_type === 'financial').reduce((sum, b) => sum + (b.annual_value || 0), 0))}
              </p>
              <p className="text-xs text-gray-400">{benefits.filter(b => b.benefit_type === 'financial').length} benefits</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-xs text-gray-500 uppercase mb-1">Non-Financial</p>
              <p className="text-xl font-bold text-blue-600">
                {benefits.filter(b => b.benefit_type !== 'financial').length}
              </p>
              <p className="text-xs text-gray-400">qualitative benefits</p>
            </div>
            {/* By Confidence */}
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-xs text-gray-500 uppercase mb-1">High Confidence</p>
              <p className="text-xl font-bold text-emerald-600">
                {formatCurrency(benefits.filter(b => b.confidence === 'high' && b.benefit_type === 'financial').reduce((sum, b) => sum + (b.annual_value || 0), 0))}
              </p>
              <p className="text-xs text-gray-400">{benefits.filter(b => b.confidence === 'high').length} benefits</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-xs text-gray-500 uppercase mb-1">Source</p>
              <p className="text-xl font-bold text-purple-600">
                {benefits.filter(b => b.source_requirement_name).length}
              </p>
              <p className="text-xs text-gray-400">from requirements</p>
            </div>
          </div>
          {/* Benefits with sources - show top 3 */}
          {benefits.filter(b => b.source_requirement_name).length > 0 && (
            <div className="mt-3 pt-3 border-t border-green-200">
              <p className="text-xs text-gray-500 uppercase mb-2">Synced from Requirements</p>
              <div className="space-y-1">
                {benefits.filter(b => b.source_requirement_name).slice(0, 3).map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-white px-2 py-1 rounded">
                    <span className="text-gray-700 truncate">{b.name || b.description || 'Unnamed benefit'}</span>
                    <span className="text-green-600 font-medium">{formatCurrency(b.annual_value || 0)}</span>
                  </div>
                ))}
                {benefits.filter(b => b.source_requirement_name).length > 3 && (
                  <p className="text-xs text-gray-400 text-center">+{benefits.filter(b => b.source_requirement_name).length - 3} more from requirements</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Labor Costs Section */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Labor Costs
            <HelpTooltip topic="labor" />
          </h3>
          <button
            onClick={addLaborCost}
            disabled={readOnly}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50"
            data-testid="add-labor-btn"
          >
            <Plus className="w-4 h-4" />
            Add Role
          </button>
        </div>
        <div className="p-4">
          {laborCosts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No labor costs defined</p>
              <p className="text-sm">Click &ldquo;Add Role&rdquo; or use &ldquo;AI Suggest&rdquo; to get started</p>
            </div>
          ) : (
            laborCosts.map((item, index) => (
              <LaborCostRow
                key={index}
                item={item}
                index={index}
                onUpdate={updateLaborCost}
                onRemove={removeLaborCost}
                formatCurrency={formatCurrency}
              />
            ))
          )}
        </div>
      </div>

      {/* Non-Labor Costs Section */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-amber-600" />
            Non-Labor Costs
            <HelpTooltip topic="nonLabor" />
          </h3>
          <button
            onClick={addNonLaborCost}
            disabled={readOnly}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 disabled:opacity-50"
            data-testid="add-nonlabor-btn"
          >
            <Plus className="w-4 h-4" />
            Add Cost
          </button>
        </div>
        <div className="p-4">
          {nonLaborCosts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calculator className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No non-labor costs defined</p>
            </div>
          ) : (
            nonLaborCosts.map((item, index) => (
              <NonLaborCostRow
                key={index}
                item={item}
                index={index}
                onUpdate={updateNonLaborCost}
                onRemove={removeNonLaborCost}
                currencySymbol={currencySymbol}
              />
            ))
          )}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Benefits
            <HelpTooltip topic="benefits" />
          </h3>
          <button
            onClick={addBenefit}
            disabled={readOnly}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50"
            data-testid="add-benefit-btn"
          >
            <Plus className="w-4 h-4" />
            Add Benefit
          </button>
        </div>
        <div className="p-4">
          {benefits.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No benefits defined</p>
            </div>
          ) : (
            benefits.map((item, index) => (
              <BenefitRow
                key={index}
                item={item}
                index={index}
                onUpdate={updateBenefit}
                onRemove={removeBenefit}
                currencySymbol={currencySymbol}
              />
            ))
          )}
        </div>
      </div>

      {/* Assumptions Section */}
      <div className="bg-white border rounded-lg">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => setShowAssumptions(!showAssumptions)}
        >
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Info className="w-5 h-5 text-gray-500" />
            Assumptions ({assumptions.length})
          </h3>
          {showAssumptions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
        {showAssumptions && (
          <div className="p-4 border-t">
            {assumptions.length === 0 ? (
              <p className="text-gray-500 text-sm">No assumptions recorded</p>
            ) : (
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                {assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Unsaved Changes Warning */}
      {dirty && (
        <div className="fixed bottom-4 right-4 bg-amber-100 border border-amber-300 rounded-lg p-3 flex items-center gap-3 shadow-lg">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <span className="text-sm text-amber-800">You have unsaved changes</span>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
          >
            {saving ? 'Saving...' : 'Save Now'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FinancialEstimateTab;
